import React, { useCallback, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * Dropdown de acciones para conversaciones (renderizado vía Portal)
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isVisible - Controla la visibilidad del dropdown
 * @param {Object} props.position - Posición absoluta {top, left}
 * @param {string} props.timestamp - ID único de la conversación
 * @param {string} props.themeMode - 'dark' | 'light'
 * @param {boolean} props.isDeleting - Estado de confirmación de eliminación
 * @param {Function} props.onDelete - Handler para eliminación
 * @param {React.Ref} ref - Ref forwarded desde el padre
 */
const ConversationDropdown = forwardRef(({
    isVisible,
    position,
    timestamp,
    themeMode,
    isDeleting,
    onDelete
}, ref) => {
    const { t } = useTranslation();

    // ✅ Handler optimizado con useCallback
    const handleDelete = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        onDelete(e, timestamp);
    }, [timestamp, onDelete]);

    // ⚠️ Early return si no es visible
    if (!isVisible) return null;

    const dropdownAnimation = `
    @keyframes slideIn {
        from {
            opacity: 0;
            transform: translateY(-8px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    `;

    return createPortal(
        <div
            ref={ref} // ✅ Ref forwarded - crítico para useClickOutside
            className="dropdown-menu-chat"
            style={{
                position: 'fixed',
                top: `${position.top}px`,
                left: `${position.left}px`,
                zIndex: 999999,
                backgroundColor: themeMode === 'dark' ? '#202123' : '#f7f7f8',
                border: `1px solid ${themeMode === 'dark' ? '#565869' : '#d9d9e3'}`,
                borderRadius: '6px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                minWidth: '150px',
                padding: '5px',
                animation: 'slideIn 0.15s ease-out',
                transformOrigin: 'top left'
            }}
            onClick={(e) => e.stopPropagation()}
            role="menu"
            aria-label={t('chat.dropdown_aria_label', 'Conversation actions')}
        >
            <button
                className={`dropdown-item-chat delete-chat ${isDeleting ? 'confirming' : ''}`}
                onClick={handleDelete}
                role="menuitem"
                aria-label={isDeleting
                    ? t('chat.sidebar_delete_confirmation', 'Confirm deletion')
                    : t('chat.sidebar_delete', 'Delete')
                }
                style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 16px',
                    fontSize: '14px',
                    border: 'none',
                    background: isDeleting ? '#c0392b' : 'transparent',
                    cursor: 'pointer',
                    color: isDeleting ? 'white' : '#e74c3c',
                    borderRadius: '4px',
                    transition: 'all 0.2s',
                    fontWeight: isDeleting ? 'bold' : 'normal'
                }}
                onMouseEnter={(e) => {
                    if (!isDeleting) {
                        e.currentTarget.style.backgroundColor = themeMode === 'dark' ? '#2A2B32' : '#ececf1';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isDeleting) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }
                }}
            >
                {isDeleting
                    ? t('chat.sidebar_delete_confirmation', 'Confirm deletion')
                    : t('chat.sidebar_delete', 'Delete')
                }
            </button>
        </div>,
        document.body
    );
});

// ✅ Nombre de display para React DevTools
ConversationDropdown.displayName = 'ConversationDropdown';

export default React.memo(ConversationDropdown);